import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { IconInstallationCreated } from '../../../../assets/status-screen-icons/icon-installation-created';
import { DialogConfirmation } from '../../../../components';
import { PopupNotificationMarks } from '../../../../const';
import { useBackdropContext } from '../../../../context/backdrop';
import { useNativeFunctionsContext } from '../../../../context/native-functions';
import {
  MarkPopupNotificationDisplayedMutation,
  MarkPopupNotificationDisplayedMutationVariables,
} from '../../../../data-access/gql-types/graphql';
import { MARK_POPUP_NOTIFICATION_DISPLAYED } from '../../../../data-access/mutations/user';
import { useExalusServicesContext } from '../../../../integrations/exalus/context/services';
import * as storage from '../../../../integrations/lavva/storage';
import { ROUTES } from '../../../../routes';
import { useUserProfileStore } from '../../../../store/user-profile';

type EvaluateAppDialogProps = {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
};

export const EvaluateAppDialog: React.FC<EvaluateAppDialogProps> = ({ showDialog, setShowDialog }) => {
  const navigate = useNavigate();
  const { t: tc } = useTranslation('common');
  const refetchUserProfile = useUserProfileStore((s) => s.refetchUserProfile);
  const { turnOnBackdrop, turnOffBackdrop } = useBackdropContext();
  const { appRateApi } = useExalusServicesContext();
  const { setEvaluateAppPopupAvailable } = useNativeFunctionsContext();
  const [markPopupAsDisplayed] = useMutation<
    MarkPopupNotificationDisplayedMutation,
    MarkPopupNotificationDisplayedMutationVariables
  >(MARK_POPUP_NOTIFICATION_DISPLAYED);

  const hideEvaluatePopup = () => {
    setShowDialog(false);
    setEvaluateAppPopupAvailable(false);
  };

  const handlePrimaryBtn = () => {
    turnOnBackdrop();

    markPopupAsDisplayed({
      variables: {
        input: {
          mark: {
            at: new Date(),
            key: PopupNotificationMarks.EvaluateDialog,
          },
        },
      },
      onCompleted: async () => {
        turnOffBackdrop();
        hideEvaluatePopup();

        await appRateApi.OpenRateDialogAsync();
        refetchUserProfile();
      },
      onError: () => {
        turnOffBackdrop();
      },
    });
  };

  const handleSecondaryBtn = () => {
    storage.setItem('remindEvaluate', new Date());
    hideEvaluatePopup();

    navigate(ROUTES.Support());
  };

  const handleTertiaryBtn = () => {
    storage.setItem('remindEvaluate', new Date());
    hideEvaluatePopup();
  };

  return (
    <DialogConfirmation
      show={showDialog}
      setShow={() => setShowDialog(false)}
      header={tc('evaluate.evaluatePopup.header')}
      primaryBtnText={tc('buttons.yes')}
      secondaryBtnText={tc('buttons.no')}
      tertiaryBtnText={tc('buttons.later')}
      primaryBtnAction={handlePrimaryBtn}
      secondaryBtnAction={handleSecondaryBtn}
      tertiaryBtnAction={handleTertiaryBtn}
    >
      <IconInstallationCreated style={{ width: '100%', height: '120px' }} />
      <p>{tc('evaluate.evaluatePopup.content')}</p>
    </DialogConfirmation>
  );
};
